import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, Check, Sparkles, Rocket, Building2 } from "lucide-react";
import { useAuth } from "@/lib/auth";

const TIER_META = {
  moxyplay_starter: {
    icon: Sparkles,
    tone: "text-cyan-300",
    highlights: ["1 channel", "HD 1080p", "Community support", "30-day retention"],
  },
  moxyplay_pro: {
    icon: Rocket,
    tone: "text-fuchsia-300",
    recommended: true,
    highlights: ["10 channels", "4K UHD", "SCTE-35 + SSAI", "Priority support", "1-year retention"],
  },
  moxyplay_enterprise: {
    icon: Building2,
    tone: "text-amber-300",
    highlights: ["Unlimited channels", "White-glove onboarding", "Multi-region CDN", "SLA + dedicated SRE"],
  },
};

/** UpgradePlanDialog — pulls the live Stripe catalog and creates a checkout session. */
export default function UpgradePlanDialog({ open, onClose }) {
  const { user, tenant } = useAuth();
  const [tiers, setTiers] = useState([]);
  const [interval, setInterval] = useState("month");
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/payments/catalog")
      .then((r) => setTiers(r.data.tiers || []))
      .catch(() => toast.error("Failed to load plans"))
      .finally(() => setLoading(false));
  }, [open]);

  const startCheckout = async (lookupKey) => {
    setCheckingOut(lookupKey);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: lookupKey,
        quantity: 1,
        origin_url: window.location.origin,
        user_id: user?.id,
        tenant_id: tenant?.id,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Checkout could not start");
      setCheckingOut(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="bg-card border-border rounded-sm max-w-4xl max-h-[90vh] overflow-y-auto"
        data-testid="upgrade-plan-dialog"
      >
        <DialogHeader className="relative pb-4 mb-2">
          <div className="absolute inset-x-0 -top-6 -mx-6 h-1" style={{ background: "var(--brand-gradient)" }} />
          <DialogTitle className="font-display text-2xl">Upgrade your plan</DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground font-mono">
            Trial → paid. Cancel any time from the Stripe customer portal. Prices in USD, taxes handled by Stripe.
          </DialogDescription>
        </DialogHeader>

        {/* Interval toggle */}
        <div className="flex justify-center mb-6">
          <div className="inline-flex gradient-stroke rounded-full p-1 gap-1">
            {["month", "year"].map((i) => (
              <button
                key={i}
                onClick={() => setInterval(i)}
                data-testid={`interval-${i}`}
                className={`px-4 py-1.5 rounded-full text-xs font-display font-bold uppercase tracking-widest transition ${
                  interval === i ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {i === "month" ? "Monthly" : "Yearly (save ~17%)"}
              </button>
            ))}
          </div>
        </div>

        {loading && (
          <div className="py-16 flex items-center justify-center text-muted-foreground text-sm">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading plans…
          </div>
        )}

        {!loading && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {tiers.map((t) => {
              const meta = TIER_META[t.id] || {};
              const Icon = meta.icon || Sparkles;
              const price = t.prices.find((p) => p.interval === interval);
              if (!price) return null;
              const dollars = (price.amount_cents / 100).toLocaleString("en-US", { style: "currency", currency: price.currency.toUpperCase(), maximumFractionDigits: 0 });
              return (
                <div
                  key={t.id}
                  className={`relative panel rounded-2xl p-5 flex flex-col ${meta.recommended ? "ring-2 ring-fuchsia-400/50" : ""}`}
                  data-testid={`plan-card-${t.id}`}
                >
                  {meta.recommended && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full px-3 py-0.5 text-[9px] font-mono uppercase tracking-widest text-white" style={{ background: "var(--brand-gradient)" }}>
                      Recommended
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Icon className={`w-5 h-5 ${meta.tone || "text-foreground"}`} strokeWidth={1.5} />
                    <div className="font-display font-black text-lg">{t.name.replace("MOXYPLAY ", "")}</div>
                  </div>
                  <div className="mt-4 flex items-baseline gap-1">
                    <div className="font-display font-black text-3xl">{dollars}</div>
                    <div className="text-xs text-muted-foreground">/ {interval}</div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 min-h-[32px]">{t.description}</p>
                  <ul className="mt-4 space-y-2 flex-1">
                    {(meta.highlights || []).map((h) => (
                      <li key={h} className="flex items-start gap-2 text-sm">
                        <Check className="w-3.5 h-3.5 text-emerald-400 mt-1 shrink-0" />
                        <span className="text-foreground/85">{h}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    onClick={() => startCheckout(price.lookup_key)}
                    disabled={!!checkingOut}
                    data-testid={`upgrade-${t.id}-${interval}`}
                    className="mt-5 tenant-btn rounded-full h-10 font-display font-bold text-sm"
                  >
                    {checkingOut === price.lookup_key ? (
                      <><Loader2 className="w-3.5 h-3.5 animate-spin mr-2" /> Redirecting…</>
                    ) : (
                      <>Upgrade to {t.name.replace("MOXYPLAY ", "")}</>
                    )}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
        <div className="mt-6 text-[10px] font-mono uppercase tracking-widest text-muted-foreground text-center">
          Stripe manages tax and compliance handling on this plan.
        </div>
      </DialogContent>
    </Dialog>
  );
}
